import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import colors from "common/styles/colors";
import { Typography } from "common/components/Typography";
import {
  MatchRateCounts,
  MatchRateScope,
  MatchRateSummary,
} from "../../types/matchRate.types";

interface MatchRateSummaryCardsProps {
  summary: MatchRateSummary;
}

const scopes: MatchRateScope[] = ["search", "details", "combined"];

const formatRate = (rate: number | null) => {
  if (rate == null) {
    return "—";
  }
  return `${(rate * 100).toFixed(2)}%`;
};

const MetricRow: React.FC<{
  label: string;
  rate: number | null;
  matched: number;
  total: number;
}> = ({ label, rate, matched, total }) => {
  return (
    <div style={{ marginBottom: "12px" }}>
      <Typography
        variant="caption"
        textStyle={{ color: colors.grey, fontSize: 13 }}
      >
        {label}
      </Typography>
      <Typography textStyle={{ fontSize: 22, fontWeight: "600" }}>
        {formatRate(rate)}
      </Typography>
      <Typography
        variant="caption"
        textStyle={{ color: colors.grey, fontSize: 13 }}
      >
        {matched} / {total}
      </Typography>
    </div>
  );
};

const ScopeCard: React.FC<{
  scope: MatchRateScope;
  counts: MatchRateCounts;
}> = ({ scope, counts }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        border: `1px solid ${colors.greyborder}`,
        borderRadius: "10px",
        padding: "20px",
        backgroundColor: colors.bg,
        minWidth: 0,
      }}
    >
      <Typography variant="h2" textStyle={{ fontSize: 18, marginBottom: 16 }}>
        {t(`matchRate.scopes.${scope}`)}
      </Typography>
      <Typography
        variant="caption"
        textStyle={{ color: colors.grey, fontSize: 13, marginBottom: 12 }}
      >
        {t(`matchRate.scopeHints.${scope}`)}
      </Typography>
      <MetricRow
        label={t("matchRate.listingMatch")}
        rate={counts.listing_match_rate}
        matched={counts.listing_matched}
        total={counts.total}
      />
      <MetricRow
        label={t("matchRate.projectMatch")}
        rate={counts.project_match_rate}
        matched={counts.project_matched}
        total={counts.project_eligible}
      />
      <MetricRow
        label={t("matchRate.anyMatch")}
        rate={counts.any_match_rate}
        matched={counts.any_matched}
        total={counts.total}
      />
    </Box>
  );
};

export const MatchRateSummaryCards: React.FC<MatchRateSummaryCardsProps> = ({
  summary,
}) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "repeat(3, minmax(0, 1fr))",
        },
        gap: "16px",
        marginBottom: "24px",
      }}
    >
      {scopes.map((scope) => (
        <ScopeCard key={scope} scope={scope} counts={summary[scope]} />
      ))}
    </Box>
  );
};
